import { LockIcon } from "@xola/icons";
import { isArray } from "lodash";
import React from "react";
import { Audit, Purchase, PurchaseItem } from "types";
import { TimelineItem, TimelineItemReason } from "../TimelineItem";
import { useInstalledPlugins } from "@/hooks/usePlugins";

interface QuestionnaireUpdatedLineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
}

export const QuestionnaireUpdatedLineItem = ({ purchase, item, audit }: QuestionnaireUpdatedLineItemProps) => {
    if (!QuestionnaireUpdatedLineItem.supportsAudit(purchase, item, audit)) {
        return null;
    }

    return <QuestionnaireUpdatedLineItemContent purchase={purchase} audit={audit} item={item} />;
};

const QuestionnaireUpdatedLineItemContent = ({ purchase, audit, item }: QuestionnaireUpdatedLineItemProps) => {
    const auditCreatedBy = audit?.createdBy?.id ?? null;
    const { data: plugins } = useInstalledPlugins();
    const hasWaiverChanges = hasWaiverChangesInAudit(audit, item);
    const isUpdatedByPlugin =
        hasWaiverChanges || (auditCreatedBy && plugins.some((plugin) => plugin.user?.id === auditCreatedBy));

    const title = isUpdatedByPlugin ? "Guest Data Updated" : "Questionnaire Answers";
    const description = isUpdatedByPlugin
        ? "Guest details were updated using data from a signed waiver"
        : "Questionnaire answers were updated";

    return (
        <TimelineItem title={title} audit={audit} icon={<LockIcon />} color="primary" purchase={purchase}>
            <div className="items-center">
                <TimelineItemReason localize>{description}</TimelineItemReason>
            </div>
        </TimelineItem>
    );
};

QuestionnaireUpdatedLineItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit) => {
    if (!isArray(audit.data?.items)) {
        return null;
    }

    const auditItem = audit?.data?.items?.find((auditItem: any) => auditItem.id === item?.id);
    return (
        (auditItem?.guests && auditItem.guests.some((guest: any) => hasFieldsInFormResponse(guest.formResponse))) ||
        hasFieldsInFormResponse(auditItem?.formResponse)
    );
};

const hasFieldsInFormResponse = (formResponse: any) => {
    if (!formResponse) {
        return false;
    }

    // If questionnaire is newly added, we will add in this format -> new.fields
    if (formResponse.new?.fields && Array.isArray(formResponse.new?.fields)) {
        return formResponse.new?.fields.length > 0;
    }

    // If questionnaire is updated, we will get the values in this format -> fields.2.new
    if (formResponse.fields && typeof formResponse.fields === "object") {
        return !!formResponse.fields;
    }

    return false;
};

const hasWaiverChangesInAudit = (audit: Audit, item: PurchaseItem) => {
    if (!isArray(audit.data?.items)) {
        return false;
    }

    const auditItem = audit?.data?.items?.find((auditItemData: any) => auditItemData.id === item?.id);

    if (auditItem?.waivers) {
        return true;
    }

    return auditItem?.guests && auditItem.guests.some((guest: any) => !!guest.waiver);
};
